/**
 * Divider — React (web) implementation. A decorative rule, hidden from
 * assistive tech. Rendered as a div (not <hr>) so no user-agent margin
 * needs resetting on the component root (ADR-020 Rule 2).
 */

/// <reference lib="dom" />

import React from 'react';
import {
  DIVIDER_COLOR,
  DIVIDER_DASHED_COLOR,
  DIVIDER_DASHED_WEIGHT,
  DIVIDER_SOLID_WEIGHT,
  DIVIDER_VERTICAL_WIDTH,
  DIVIDER_VERTICAL_RADIUS,
} from './Divider';
import type { DividerLine, DividerOrientation } from './Divider';

export interface WebDividerProps {
  orientation?: DividerOrientation;
  line?: DividerLine;
  color?: string;
  'data-testid'?: string;
}

export function Divider({
  orientation = 'horizontal',
  line = 'solid',
  color,
  'data-testid': testId,
}: WebDividerProps) {
  const style: React.CSSProperties =
    orientation === 'vertical'
      ? {
          width: DIVIDER_VERTICAL_WIDTH,
          height: '100%',
          borderRadius: DIVIDER_VERTICAL_RADIUS,
          backgroundColor: color ?? DIVIDER_COLOR,
        }
      : line === 'dashed'
        ? {
            width: '100%',
            height: 0,
            borderTop: `${DIVIDER_DASHED_WEIGHT}px dashed ${color ?? DIVIDER_DASHED_COLOR}`,
          }
        : {
            width: '100%',
            height: DIVIDER_SOLID_WEIGHT,
            backgroundColor: color ?? DIVIDER_COLOR,
          };

  return <div aria-hidden="true" data-testid={testId} style={style} />;
}
